import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Truck, CreditCard, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type AdminOrder } from "@/types/admin.ui";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
  type DataTableFilterConfig,
} from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import { useAdminOrders, useUpdateOrderStatusMutation } from "@/hooks/queries";
import { buildOrderStatusUpdate } from "@/lib/mappers/order-mappers";
import { getApiErrorMessage } from "@/services/httpClient";
import { Skeleton } from "@/components/ui/skeleton";

const statusColor: Record<string, string> = {
  placed: "bg-info/10 text-info border-info/20",
  confirmed: "bg-warning/10 text-warning border-warning/20",
  dispatched: "bg-primary/10 text-primary border-primary/20",
  delivered: "bg-success/10 text-success border-success/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

const statusFilters: DataTableFilterConfig[] = [
  {
    id: "status",
    placeholder: "Status",
    options: [
      { label: "All Status", value: "all" },
      { label: "Placed", value: "placed" },
      { label: "Confirmed", value: "confirmed" },
      { label: "Dispatched", value: "dispatched" },
      { label: "Delivered", value: "delivered" },
      { label: "Cancelled", value: "cancelled" },
    ],
  },
];

const ORDER_STATUSES = ["placed", "confirmed", "dispatched", "delivered", "cancelled"] as const;

const AdminOrders = () => {
  const { toast } = useToast();
  const { data, isLoading } = useAdminOrders({ pageSize: 100 });
  const updateStatusMutation = useUpdateOrderStatusMutation();
  const orders = data?.items ?? [];

  const table = useDataTable<AdminOrder>({
    data: orders,
    searchKeys: ["orderId", "customerName", "storeName"],
    filterConfigs: statusFilters,
    filterPredicate: (row, state) =>
      state.filters.status === "all" || row.status === state.filters.status,
  });

  const activeOrders = orders.filter(
    (o) => o.status !== "delivered" && o.status !== "cancelled",
  ).length;

  const updateStatus = async (id: string, status: AdminOrder["status"]) => {
    try {
      await updateStatusMutation.mutateAsync({
        id: Number(id),
        dto: buildOrderStatusUpdate(status),
      });
      toast({ title: "Order status updated", description: `Status changed to ${status}` });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const columns: DataTableColumn<AdminOrder>[] = [
      {
        id: "orderId",
        header: "Order ID",
        accessorKey: "orderId",
        sortable: true,
        mobilePriority: 1,
        cell: (order) => (
          <Link to={`/admin/orders/${order.id}`} className="font-semibold text-primary hover:underline">
            {order.orderId}
          </Link>
        ),
      },
      {
        id: "customerName",
        header: "Customer",
        accessorKey: "customerName",
        sortable: true,
        mobileLabel: "Customer",
        mobilePriority: 2,
      },
      {
        id: "storeName",
        header: "Store",
        accessorKey: "storeName",
        sortable: true,
        mobileLabel: "Store",
        mobilePriority: 3,
        className: "text-sm",
      },
      {
        id: "items",
        header: "Items",
        accessorKey: "items",
        sortable: true,
        align: "center",
        mobileLabel: "Items",
        mobilePriority: 4,
      },
      {
        id: "total",
        header: "Total",
        accessorKey: "total",
        sortable: true,
        align: "right",
        mobileLabel: "Total",
        mobilePriority: 5,
        cell: (order) => <span className="font-medium">₹{order.total}</span>,
      },
      {
        id: "paymentMethod",
        header: "Payment",
        accessorKey: "paymentMethod",
        hideOnMobile: true,
        cell: (order) => (
          <Badge variant="secondary" className="text-xs">
            {order.paymentMethodText}
          </Badge>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        sortable: true,
        mobileLabel: "Status",
        mobilePriority: 6,
        cell: (order) => (
          <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v as AdminOrder["status"])}>
            <SelectTrigger className="w-[130px] h-8">
              <Badge variant="outline" className={statusColor[order.status]}>
                {order.status}
              </Badge>
            </SelectTrigger>
            <SelectContent>
              {ORDER_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      },
      {
        id: "orderDate",
        header: "Date",
        accessorKey: "orderDate",
        sortable: true,
        hideOnMobile: true,
        className: "text-sm text-muted-foreground",
      },
      {
        id: "actions",
        header: "Actions",
        align: "center",
        hideOnMobile: true,
        cell: (order) => (
          <Button size="icon" variant="ghost" className="h-8 w-8" asChild>
            <Link to={`/admin/orders/${order.id}`}>
              <Eye className="h-4 w-4" />
            </Link>
          </Button>
        ),
      },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Order Management" description="Monitor and manage all platform orders." />

      <StatsGrid columns={4}>
        <CompactStatCard title="Total Orders" value={isLoading ? "…" : (data?.totalItems ?? orders.length).toLocaleString()} icon={ShoppingCart} />
        <CompactStatCard title="Active Orders" value={isLoading ? "…" : activeOrders} icon={Truck} />
        <CompactStatCard title="Listed Orders" value={isLoading ? "…" : orders.length} icon={ShoppingCart} />
        <CompactStatCard title="Delivered" value={isLoading ? "…" : orders.filter((o) => o.status === "delivered").length} icon={CreditCard} />
      </StatsGrid>

      {isLoading ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
      <DataTable
        title="All Orders"
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search orders..."
        filters={statusFilters}
        filterValues={table.state.filters}
        onFilterChange={table.setFilter}
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        renderMobileCard={(order) => (
          <div className="rounded-lg border border-border p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-semibold">{order.orderId}</p>
                <p className="text-xs text-muted-foreground">{order.customerName}</p>
                <p className="text-xs text-muted-foreground">{order.storeName}</p>
              </div>
              <Badge variant="outline" className={statusColor[order.status]}>
                {order.status}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {order.items} items · {order.paymentMethodText}
              </span>
              <span className="font-bold">₹{order.total}</span>
            </div>
            <div className="flex gap-2">
              <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v as AdminOrder["status"])}>
                <SelectTrigger className="flex-1 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" asChild>
                <Link to={`/admin/orders/${order.id}`}>
                  <Eye className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        )}
      />

      )}
    </div>
  );
};

export default AdminOrders;
